import { keccak256 } from 'js-sha3';
import type { RawSeedData } from './rpc.types';
import { SEED_SPECS } from './seed-specs';
import {
  addCalendarMonthsUtc,
  readSeasonTuple,
  SEASON_SEED_PREFIXES,
  type SeasonSeedKey,
  type WindowSelection,
} from './seed-window';

export const SEED_WINDOW_BUDGET = 800 * 1024;
export const SEED_WINDOW_WARNING = 700 * 1024;
export interface SeedPlanContext {
  sourceVersion: string;
  budgetBytes?: number;
  allowChanges?: boolean;
  completionMarkers?: Record<string, unknown>;
}
export interface SeedWindowPlan {
  planId: string;
  baseFingerprint: string;
  sourceVersion: string;
  upserts: Record<string, unknown>;
  expiredDeletes: string[];
  estimatedFinalBytes: number;
  estimatedPeakBytes: number;
  finalState: Record<string, unknown>;
  now: number;
  warning: boolean;
}

/** JSON 데이터만 허용하며 객체 키 순서를 정규화한다. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value === 'string' || typeof value === 'boolean')
    return JSON.stringify(value);
  if (typeof value === 'number' && Number.isFinite(value)) return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (typeof value === 'object' && value !== null)
    return `{${Object.keys(value)
      .sort()
      .map(
        (key) => `${JSON.stringify(key)}:${canonicalJson((value as Record<string, unknown>)[key])}`
      )
      .join(',')}}`;
  throw new Error('유효한 JSON 데이터가 필요합니다');
}
export function stateFingerprint(state: unknown): string {
  return keccak256(canonicalJson(state));
}

/** 실제 저장 크기와 동일하지 않은 보수적 추정치. UTF-8·필드 오버헤드·여유분 포함. */
export function estimateStateBytes(state: Record<string, unknown>): number {
  const json = canonicalJson(state);
  let bytes = 0;
  for (const char of json) {
    const cp = char.codePointAt(0) ?? 0;
    bytes += cp <= 0x7f ? 1 : cp <= 0x7ff ? 2 : cp <= 0xffff ? 3 : 4;
  }
  return Math.ceil(bytes * 1.2) + Object.keys(state).length * 32 + 256;
}

export function seedDataToState(data: RawSeedData): Record<string, unknown> {
  const entries: [string, unknown][] = [];
  for (const [key, spec] of Object.entries(SEED_SPECS)) {
    const value = (data as Record<string, unknown>)[key];
    if (value === undefined) continue;
    if (spec.kind !== 'indexed') {
      if (
        value == null ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
      )
        continue;
      if (value !== null) entries.push([spec.key, value]);
      continue;
    }
    if (!Array.isArray(value)) throw new Error(`튜플 배열이 필요합니다: ${key}`);
    const ids = new Set<string>();
    for (const row of value) {
      if (
        !Array.isArray(row) ||
        !['string', 'number'].includes(typeof row[0]) ||
        String(row[0]) === '' ||
        String(row[0]) === '__proto__'
      )
        throw new Error(`튜플 ID가 올바르지 않습니다: ${key}`);
      const id = String(row[0]);
      if (ids.has(id)) throw new Error(`중복 ID: ${key}/${id}`);
      ids.add(id);
      entries.push([`${spec.prefix}${id}`, row]);
    }
  }
  if (data.gameConfig)
    for (const [key, value] of Object.entries(data.gameConfig)) entries.push([`gc.${key}`, value]);
  const result = Object.fromEntries(entries);
  canonicalJson(result);
  return result;
}

export function buildWindowPlan(
  current: Record<string, unknown>,
  selected: WindowSelection,
  context: SeedPlanContext
): SeedWindowPlan {
  if (!context.sourceVersion) throw new Error('원본 버전이 필요합니다');
  const desired = seedDataToState(selected.data);
  const markerKeys = Object.keys(context.completionMarkers ?? {});
  if (
    markerKeys.some((key) => !['masterData.seedWindow', 'masterData.activeManifest'].includes(key))
  )
    throw new Error('허용되지 않은 완료 표식입니다');
  Object.assign(desired, context.completionMarkers);
  const sourceState = seedDataToState(selected.source);
  const expiredDeletes: string[] = [];
  for (const [category, prefix] of Object.entries(SEASON_SEED_PREFIXES)) {
    if (selected.data[category as SeasonSeedKey] === undefined) continue;
    for (const [key, value] of Object.entries(current)) {
      if (!key.startsWith(prefix)) continue;
      const season = readSeasonTuple(value, category as SeasonSeedKey);
      if (key !== `${prefix}${season.id}`) throw new Error(`시즌 키와 ID 불일치: ${key}`);
      if (selected.now >= addCalendarMonthsUtc(season.endTime, 2)) {
        expiredDeletes.push(key);
        continue;
      }
      if (!(key in desired)) {
        if (
          season.startTime > addCalendarMonthsUtc(selected.now, 6) &&
          Object.hasOwn(sourceState, key) &&
          canonicalJson(sourceState[key]) === canonicalJson(value)
        ) {
          expiredDeletes.push(key);
          continue;
        }
        throw new Error(`보존 중인 시즌이 원본에서 누락됐습니다: ${key}`);
      }
    }
  }
  const upserts: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(desired)) {
    if (key in current && canonicalJson(current[key]) === canonicalJson(value)) continue;
    if (current[key] != null && !context.allowChanges && !markerKeys.includes(key))
      throw new Error(`기존 설정 변경은 점검 중 수동 시딩이 필요합니다: ${key}`);
    upserts[key] = value;
  }
  const finalState = { ...current };
  for (const key of expiredDeletes) delete finalState[key];
  Object.assign(finalState, upserts);
  const estimatedFinalBytes = estimateStateBytes(finalState);
  const afterPrune = { ...current };
  for (const key of expiredDeletes) delete afterPrune[key];
  let estimatedPeakBytes = estimateStateBytes(afterPrune);
  for (const patch of seedWriteBatches(upserts)) {
    Object.assign(afterPrune, patch);
    estimatedPeakBytes = Math.max(estimatedPeakBytes, estimateStateBytes(afterPrune));
  }
  const budget = context.budgetBytes ?? SEED_WINDOW_BUDGET;
  if (
    !Number.isFinite(budget) ||
    budget <= 0 ||
    budget > SEED_WINDOW_BUDGET ||
    estimatedPeakBytes > budget
  )
    throw new Error(`global state 용량 예산 초과: ${estimatedPeakBytes}/${budget}`);
  const baseFingerprint = stateFingerprint(current);
  const details = {
    baseFingerprint,
    sourceVersion: context.sourceVersion,
    upserts,
    expiredDeletes,
    now: selected.now,
  };
  return {
    ...details,
    planId: stateFingerprint(details),
    finalState,
    estimatedFinalBytes,
    estimatedPeakBytes,
    warning: estimatedFinalBytes >= SEED_WINDOW_WARNING,
  };
}

/** 계획과 실제 적용이 같은 청크 순서를 공유한다. 완료 표식은 별도 마지막 청크다. */
export function seedWriteBatches(upserts: Record<string, unknown>): Record<string, unknown>[] {
  const entries = Object.entries(upserts);
  const groups = [
    entries.filter(([key]) => !key.startsWith('masterData.')),
    entries.filter(([key]) => key.startsWith('masterData.')),
  ];
  const batches: Record<string, unknown>[] = [];
  for (const group of groups)
    for (let i = 0; i < group.length; i += 100)
      batches.push(Object.fromEntries(group.slice(i, i + 100)));
  return batches;
}
